import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BackButton } from '@/components/shared/BackButton';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchTemplates, deleteTemplate, type EmailTemplate } from '../../lib/inboxTemplatesApi';
import { QueryErrorState } from '@/components/shared/QueryErrorState';

const CATEGORY_LABELS: Record<string, string> = {
  general: 'General',
  fee_notes: 'Fee Notes',
  hearings: 'Hearings',
  conferences: 'Conferences',
  intake: 'Intake',
  correspondence: 'Correspondence',
};

export default function InboxTemplatesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState('');

  const { data: templates = [], isLoading, isError, refetch } = useQuery({
    queryKey: ['inbox-templates'],
    queryFn: fetchTemplates,
    // Cross-device freshness. `invalidateQueries` only refreshes the device that made the
    // change, and the inbox module emits no socket events, so a second device sat on its cached
    // list until the person refreshed by hand (seen: a signature added on a phone never appeared
    // on the open desktop tab). Polling only runs while the tab is visible — react-query's
    // `refetchIntervalInBackground` defaults to false — so an idle background tab costs nothing.
    // 15s, not 60: a minute is long enough that someone adding a signature on a phone and
    // turning to the desktop reads it as broken and refreshes by hand anyway.
    refetchInterval: 15_000,
    // Global staleTime is 60s (main.tsx:57), which would make a focus refetch a no-op for
    // the first minute. These lists are small and change rarely — always refetch.
    staleTime: 0,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTemplate,
    onSuccess: () => {
      setDeleteConfirm(null);
      qc.invalidateQueries({ queryKey: ['inbox-templates'] });
    },
  });

  const filtered = filterCategory
    ? templates.filter((t) => t.category === filterCategory)
    : templates;

  const categories = [...new Set(templates.map((t) => t.category))];

  return (
    <div style={{
      maxWidth: 760, margin: '0 auto',
      padding: 'clamp(20px, 5vw, 40px) clamp(14px, 4vw, 24px)',
      fontFamily: "'IBM Plex Sans', sans-serif",
    }}>
      {/* Was an unlabelled arrow wired to a fixed destination. These pages are opened from the
          inbox, from the Email settings hub and from Settings, so a fixed target sent people
          somewhere they had never been. Back means back — same control as every other page. */}
      <BackButton onClick={() => navigate(-1)} />

      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
            Email templates
          </h1>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: '2px 0 0' }}>
            Personal and chambers-shared templates
          </p>
        </div>
        <button
          onClick={() => navigate('/barrister/inbox/settings/templates/new')}
          style={{
            padding: '8px 18px', borderRadius: 6,
            background: '#1e3a5f', color: 'white',
            border: 'none', cursor: 'pointer',
            fontSize: 13, fontWeight: 500,
          }}
        >
          + New template
        </button>
      </div>

      {/* Category filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <button
          onClick={() => setFilterCategory('')}
          style={{
            padding: '4px 12px', borderRadius: 20,
            border: '1px solid ' + (!filterCategory ? '#1e3a5f' : '#e5e7eb'),
            background: !filterCategory ? '#1e3a5f' : 'white',
            color: !filterCategory ? 'white' : '#374151',
            cursor: 'pointer', fontSize: 12,
          }}
        >
          All
        </button>
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setFilterCategory(cat)}
            style={{
              padding: '4px 12px', borderRadius: 20,
              border: '1px solid ' + (filterCategory === cat ? '#1e3a5f' : '#e5e7eb'),
              background: filterCategory === cat ? '#1e3a5f' : 'white',
              color: filterCategory === cat ? 'white' : '#374151',
              cursor: 'pointer', fontSize: 12,
            }}
          >
            {CATEGORY_LABELS[cat] ?? cat}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>Loading…</div>
      ) : isError ? (
        /* A failed fetch would have read as "No templates" — inviting someone to recreate
           templates they already have. */
        <QueryErrorState what="your templates" onRetry={() => void refetch()} />
      ) : filtered.length === 0 ? (
        <div style={{
          border: '1px dashed #d1d5db', borderRadius: 8,
          padding: '48px 24px', textAlign: 'center', color: 'var(--text-muted)',
        }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⧉</div>
          <div style={{ fontWeight: 500, marginBottom: 6 }}>No templates</div>
          <div style={{ fontSize: 13 }}>Create your first email template to speed up correspondence.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filtered.map((t) => (
            <TemplateRow
              key={t.id}
              template={t}
              deleteConfirm={deleteConfirm}
              onEdit={() => navigate(`/barrister/inbox/settings/templates/${t.id}/edit`)}
              onDeleteRequest={() => setDeleteConfirm(t.id)}
              onDeleteConfirm={() => deleteMutation.mutate(t.id)}
              onDeleteCancel={() => setDeleteConfirm(null)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TemplateRow({
  template: t,
  deleteConfirm,
  onEdit,
  onDeleteRequest,
  onDeleteConfirm,
  onDeleteCancel,
}: {
  template: EmailTemplate;
  deleteConfirm: string | null;
  onEdit: () => void;
  onDeleteRequest: () => void;
  onDeleteConfirm: () => void;
  onDeleteCancel: () => void;
}) {
  return (
    <div style={{
      border: '1px solid var(--border-default)', borderRadius: 8,
      padding: '14px 18px', background: 'var(--surface-card)',
      display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14, marginBottom: 6,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 3 }}>
          <span style={{ fontWeight: 500, fontSize: 14, color: 'var(--text-primary)' }}>{t.name}</span>
          <span style={{
            fontSize: 10, padding: '1px 7px', borderRadius: 10,
            background: 'var(--surface-sunken)', color: 'var(--text-muted)',
          }}>
            {CATEGORY_LABELS[t.category] ?? t.category}
          </span>
          {!t.userId && (
            <span style={{
              fontSize: 10, padding: '1px 7px', borderRadius: 10,
              background: 'var(--surface-selected)', color: '#2563eb',
            }}>
              chambers
            </span>
          )}
        </div>
        <div style={{
          fontSize: 12, color: 'var(--text-muted)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {t.subject}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-subtle)', marginTop: 3 }}>
          Used {t.usageCount} time{t.usageCount !== 1 ? 's' : ''}
          {t.lastUsedAt && ` · last ${new Date(t.lastUsedAt).toLocaleDateString('en-GB')}`}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        {t.userId && (
          <>
            <button
              onClick={onEdit}
              style={{
                background: 'none', border: '1px solid var(--border-default)',
                borderRadius: 4, padding: '8px 12px',
                cursor: 'pointer', fontSize: 12, color: 'var(--text-primary)',
              }}
            >
              Edit
            </button>
            {deleteConfirm === t.id ? (
              <>
                <button
                  onClick={onDeleteConfirm}
                  style={{
                    background: '#dc2626', color: 'white',
                    border: 'none', borderRadius: 4,
                    padding: '8px 12px', cursor: 'pointer', fontSize: 12,
                  }}
                >
                  Confirm
                </button>
                <button
                  onClick={onDeleteCancel}
                  style={{
                    background: 'none', border: '1px solid var(--border-default)',
                    borderRadius: 4, padding: '8px 12px',
                    cursor: 'pointer', fontSize: 12, color: 'var(--text-primary)',
                  }}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={onDeleteRequest}
                style={{
                  background: 'none', border: 'none',
                  cursor: 'pointer', fontSize: 14, color: 'var(--text-subtle)',
                }}
              >
                ✕
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
